/**
 * ArticleShareCard — right-side share panel on every article.
 *
 * Renders:
 *   • Splink.pro short URL + copy button
 *   • QR code (auto-minted server-side)
 *   • One-tap share buttons:
 *       WhatsApp · Telegram · Twitter/X · Facebook · LinkedIn
 *       Reddit · Threads · Email · SMS · Native Share
 *   • Publish-to-social row (opens the platform's own composer with
 *     the article link + title pre-filled):
 *       Facebook · Instagram (deep-link) · LinkedIn · TikTok · Pinterest
 *
 * Backend: GET /api/publisher/v1/articles/{id}/share
 */
import { useEffect, useState } from "react";
import {
  Copy, QrCode, Loader2, Share2, Mail, MessageSquare,
  Check, Link as LinkIcon, Sparkles, Image as ImageIcon, Download,
} from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}

async function trackShare(articleId, platform) {
  try {
    await fetch(`${BASE}/articles/${articleId}/share-track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ platform }),
    });
  } catch { /* fire-and-forget */ }
}

function absUrl(pathOrUrl) {
  if (!pathOrUrl) return pathOrUrl;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${pathOrUrl}`;
}

// Brand-colored SVG icons for platforms Lucide doesn't ship coloured.
function Icon({ name, className = "w-4 h-4" }) {
  switch (name) {
    case "whatsapp":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      );
    case "telegram":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295l.213-3.053 5.56-5.023c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.643.135-.953l11.566-4.458c.538-.196 1.006.128.832.941z" />
        </svg>
      );
    case "twitter":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "facebook":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 011.141.195v3.325a8.623 8.623 0 00-.653-.036 26.805 26.805 0 00-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 00-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    case "reddit":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z" />
        </svg>
      );
    case "pinterest":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z" />
        </svg>
      );
    case "threads":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M17.36 11.13c-.09-.04-.18-.09-.28-.13-.16-3.06-1.83-4.82-4.65-4.84h-.04c-1.68 0-3.08.72-3.95 2.03l1.55 1.06c.65-.98 1.66-1.19 2.4-1.19h.03c.92.01 1.62.28 2.07.8.33.38.55.9.67 1.55-.87-.14-1.81-.19-2.83-.13-2.85.16-4.68 1.83-4.56 4.15.06 1.18.65 2.2 1.65 2.86.85.55 1.94.83 3.08.77 1.5-.08 2.68-.65 3.5-1.69.62-.79 1.02-1.81 1.19-3.09.7.42 1.22.98 1.5 1.65.49 1.14.52 3.01-1.01 4.53-1.34 1.34-2.95 1.92-5.38 1.94-2.7-.02-4.74-.89-6.06-2.58-1.24-1.59-1.88-3.88-1.9-6.81.02-2.93.66-5.22 1.9-6.81 1.32-1.69 3.36-2.56 6.06-2.58 2.72.02 4.79.9 6.16 2.6.67.84 1.18 1.89 1.51 3.12l1.86-.5c-.4-1.51-1.04-2.83-1.9-3.91C18.13 1.16 15.61.07 12.28.05h-.01c-3.33.02-5.83 1.11-7.42 3.24C3.44 5.15 2.72 7.76 2.71 11l-.01.99.01.01c.01 3.24.73 5.85 2.14 7.71 1.59 2.13 4.09 3.22 7.42 3.24h.01c2.96-.02 5.05-.79 6.77-2.51 2.25-2.25 2.19-5.07 1.44-6.8-.53-1.24-1.55-2.24-2.94-2.92l-.19-.09zm-4.62 4.84c-1.11.06-2.24-.34-2.31-1.64-.05-.96.68-2.03 2.38-2.12.19-.01.38-.02.56-.02.61 0 1.19.06 1.71.17-.19 2.36-1.29 3.55-2.34 3.61z" />
        </svg>
      );
    case "tiktok":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43V7.83a8.16 8.16 0 004.77 1.52V5.89a4.85 4.85 0 01-1.84-.2z" />
        </svg>
      );
    case "instagram":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      );
    default:
      return null;
  }
}

/** Try native share, otherwise open provider link. */
function nativeOr(url, articleId, platform) {
  return async () => {
    if (articleId && platform) trackShare(articleId, platform);
    if (navigator.share) {
      try { await navigator.share({ url }); return; } catch { /* user cancelled */ }
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };
}

export default function ArticleShareCard({ articleId, title, canonicalUrl }) {
  const [share, setShare] = useState(null);
  const [snippets, setSnippets] = useState(null);
  const [snippetSource, setSnippetSource] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [instaCopied, setInstaCopied] = useState(false);

  useEffect(() => {
    if (!articleId) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/articles/${articleId}/share`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        if (alive) setShare(await r.json());
      } catch (e) {
        if (alive) setError(e.message);
      }
    })();
    return () => { alive = false; };
  }, [articleId]);

  // Fetch AI snippets in parallel (non-blocking).
  useEffect(() => {
    if (!articleId) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/articles/${articleId}/social-snippets`);
        if (!r.ok) return;
        const d = await r.json();
        if (alive) {
          setSnippets(d.snippets || null);
          setSnippetSource(d.source || null);
        }
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [articleId]);

  const regenerateSnippets = async () => {
    const token = getToken();
    if (!token || regenerating) return;
    setRegenerating(true);
    try {
      const r = await fetch(`${BASE}/articles/${articleId}/social-snippets/regenerate`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setSnippets(d.snippets);
        setSnippetSource(d.source);
      }
    } finally {
      setRegenerating(false);
    }
  };

  const url = share?.short_url || canonicalUrl || (typeof window !== "undefined" ? window.location.href : "");
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title || "");

  // Per-platform text — prefer AI snippet, fall back to title.
  const snippetOr = (key) => (snippets && snippets[key]) || title || "";
  const shareText = (key) => encodeURIComponent(`${snippetOr(key)}${snippetOr(key) ? " — " : ""}${url}`);

  const copy = async () => {
    trackShare(articleId, "copy");
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };

  const copyInstagramKit = async () => {
    // Compose caption + link + hashtags for Instagram — best-effort pack
    const caption = snippets?.instagram || `${title || ""}\n\nRead the full story on SocialPulse.blog · link in bio.\n\n#SocialPulse #Story #Blog`;
    const fullCaption = `${caption}\n\n${url}`;
    trackShare(articleId, "instagram");
    try {
      await navigator.clipboard.writeText(fullCaption);
      setInstaCopied(true);
      setTimeout(() => setInstaCopied(false), 2400);
    } catch { /* noop */ }
    // Trigger cover image download so writer has a ready asset.
    const img = share?.cover_image_url;
    if (img) {
      const a = document.createElement("a");
      a.href = img; a.target = "_blank"; a.rel = "noopener noreferrer";
      a.download = `socialpulse-${articleId}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
    }
  };

  // Web share intents that work on desktop + mobile.
  const shareLinks = [
    { key: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${shareText("whatsapp")}`,
      brand: "bg-emerald-500 hover:bg-emerald-600 text-white" },
    { key: "telegram", label: "Telegram", href: `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(snippetOr("threads") || title || "")}`,
      brand: "bg-sky-500 hover:bg-sky-600 text-white" },
    { key: "twitter",  label: "X",        href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent(snippetOr("twitter") || title || "")}`,
      brand: "bg-slate-900 hover:bg-black text-white" },
    { key: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(snippetOr("facebook") || title || "")}`,
      brand: "bg-blue-600 hover:bg-blue-700 text-white" },
    { key: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      brand: "bg-blue-800 hover:bg-blue-900 text-white" },
    { key: "reddit",   label: "Reddit",   href: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
      brand: "bg-orange-500 hover:bg-orange-600 text-white" },
    { key: "threads",  label: "Threads",  href: `https://www.threads.net/intent/post?text=${shareText("threads")}`,
      brand: "bg-slate-900 hover:bg-black text-white" },
    { key: "pinterest", label: "Pinterest", href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}${share?.cover_image_url ? `&media=${encodeURIComponent(share.cover_image_url)}` : ""}`,
      brand: "bg-red-600 hover:bg-red-700 text-white" },
  ];

  // Publish/native flows — some are deep-links (Instagram, TikTok, Pinterest).
  const publishLinks = [
    { key: "facebook",  label: "Post to Facebook",  href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { key: "linkedin",  label: "Post to LinkedIn",  href: `https://www.linkedin.com/feed/?shareActive=true&text=${shareText("linkedin")}` },
    { key: "tiktok",    label: "Post to TikTok",    href: "https://www.tiktok.com/upload",
      note: "TikTok upload — bring your own clip; paste the article link in the caption." },
    { key: "pinterest", label: "Pin to Pinterest",  href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}${share?.cover_image_url ? `&media=${encodeURIComponent(share.cover_image_url)}` : ""}` },
  ];

  return (
    <aside
      className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4"
      data-testid="article-share-card"
      aria-label="Share this article"
    >
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 inline-flex items-center justify-center">
          <Share2 className="w-4 h-4" />
        </span>
        <div>
          <h4 className="text-sm font-bold text-slate-900 leading-tight">Share this story</h4>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            SocialPulse · Splink.pro
          </p>
        </div>
      </div>

      {/* Short URL + copy */}
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
        <div className="flex items-center gap-1.5">
          <LinkIcon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          <input
            readOnly
            value={url}
            onClick={(e) => e.target.select()}
            className="flex-1 bg-transparent text-xs text-slate-800 outline-none min-w-0 font-mono"
            data-testid="article-share-short-url"
          />
          <button
            type="button"
            onClick={copy}
            className={`inline-flex items-center gap-0.5 rounded text-[11px] font-semibold px-2 py-1 transition-colors ${
              copied
                ? "bg-emerald-100 text-emerald-800"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            data-testid="article-share-copy"
            aria-label="Copy short URL"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      {/* QR */}
      {share?.qr_png_url ? (
        <div className="flex justify-center">
          <img
            src={absUrl(share.qr_png_url)}
            alt="QR code for this article"
            className="w-32 h-32 border border-slate-200 rounded-lg bg-white"
            data-testid="article-share-qr"
          />
        </div>
      ) : share === null ? (
        <div className="flex justify-center items-center h-32 text-xs text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin mr-1" /> Generating QR…
        </div>
      ) : (
        <div className="flex justify-center items-center h-32 text-xs text-slate-400">
          <QrCode className="w-4 h-4 mr-1" /> QR unavailable
        </div>
      )}

      {/* Share row */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
          One-tap share
        </p>
        <div className="grid grid-cols-4 gap-2">
          {shareLinks.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={nativeOr(s.href, articleId, s.key)}
              title={s.label}
              aria-label={`Share on ${s.label}`}
              className={`flex flex-col items-center gap-1 rounded-lg py-2 transition-colors ${s.brand}`}
              data-testid={`article-share-${s.key}`}
            >
              <Icon name={s.key} className="w-4 h-4" />
              <span className="text-[9px] font-bold uppercase tracking-widest">{s.label}</span>
            </button>
          ))}
          <a
            href={`mailto:?subject=${encodedTitle}&body=${shareText("whatsapp")}`}
            onClick={() => trackShare(articleId, "email")}
            title="Email"
            aria-label="Share via email"
            className="flex flex-col items-center gap-1 rounded-lg py-2 bg-slate-700 hover:bg-slate-800 text-white"
            data-testid="article-share-email"
          >
            <Mail className="w-4 h-4" />
            <span className="text-[9px] font-bold uppercase tracking-widest">Email</span>
          </a>
          <a
            href={`sms:?&body=${shareText("whatsapp")}`}
            onClick={() => trackShare(articleId, "sms")}
            title="SMS"
            aria-label="Share via SMS"
            className="flex flex-col items-center gap-1 rounded-lg py-2 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="article-share-sms"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="text-[9px] font-bold uppercase tracking-widest">SMS</span>
          </a>
          <button
            type="button"
            onClick={async () => {
              trackShare(articleId, "native");
              if (navigator.share) {
                try { await navigator.share({ url, title, text: title }); } catch { /* cancelled */ }
              } else { copy(); }
            }}
            title="More"
            aria-label="More share options"
            className="flex flex-col items-center gap-1 rounded-lg py-2 bg-slate-200 hover:bg-slate-300 text-slate-800"
            data-testid="article-share-native"
          >
            <Share2 className="w-4 h-4" />
            <span className="text-[9px] font-bold uppercase tracking-widest">More</span>
          </button>
        </div>
      </div>

      {/* Instagram Copy Kit — Instagram has no web share intent, so we
          hand the writer a ready-to-paste caption + auto-download the
          cover image. */}
      <button
        type="button"
        onClick={copyInstagramKit}
        className={`w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-xs font-bold transition-colors ${
          instaCopied
            ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
            : "bg-gradient-to-r from-fuchsia-500 via-rose-500 to-amber-400 text-white hover:opacity-90"
        }`}
        data-testid="article-share-instagram-kit"
      >
        {instaCopied ? (
          <><Check className="w-4 h-4" /> Caption copied + cover downloaded</>
        ) : (
          <><Icon name="instagram" className="w-4 h-4" /> Copy Instagram caption + image</>
        )}
      </button>

      {snippets && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-2 text-[11px] text-slate-600 flex items-start gap-1.5">
          <Sparkles className={`w-3 h-3 mt-0.5 shrink-0 ${snippetSource === "llm" ? "text-amber-500" : "text-slate-400"}`} />
          <div className="flex-1 min-w-0">
            <span className="font-semibold">
              {snippetSource === "llm" ? "AI-tailored captions active" : "Default captions"}
            </span>
            <span className="text-slate-500">
              {" "}— each platform gets its own tone.
            </span>
          </div>
          <button
            type="button"
            onClick={regenerateSnippets}
            disabled={regenerating}
            className="text-blue-700 hover:text-blue-900 font-semibold shrink-0 disabled:opacity-50"
            data-testid="article-share-regenerate-snippets"
            title="Regenerate captions (writer/admin)"
          >
            {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
          </button>
        </div>
      )}

      {/* Publish row */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
          Publish on
        </p>
        <div className="grid grid-cols-1 gap-1.5">
          {publishLinks.map((p) => (
            <a
              key={p.key}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackShare(articleId, p.key)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 hover:border-slate-400 hover:bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-800"
              data-testid={`article-publish-${p.key}`}
              title={p.note || p.label}
            >
              <Icon name={p.key} className="w-4 h-4 text-slate-700" />
              <span className="flex-1">{p.label}</span>
              <span className="text-slate-400">→</span>
            </a>
          ))}
        </div>
      </div>

      {error && (
        <p className="text-[10px] text-slate-500 italic">
          Share preview unavailable — copy the link above.
        </p>
      )}
    </aside>
  );
}
